/**
 * Utility functions for handling image formats and transparency
 */

/**
 * Detects if a base64 string represents a PNG image
 * @param base64String - The base64 encoded image string
 * @returns boolean indicating if it's a PNG
 */
export const isPngBase64 = (base64String: string): boolean => {
  // PNG files start with specific base64 patterns
  return base64String.startsWith('iVBORw0KGgo') || 
         base64String.includes('PNG') ||
         base64String.startsWith('/9j/') === false; // Not JPEG
};

/**
 * Detects if a base64 string represents a JPEG image
 * @param base64String - The base64 encoded image string
 * @returns boolean indicating if it's a JPEG
 */
export const isJpegBase64 = (base64String: string): boolean => {
  // JPEG files start with specific base64 patterns
  return base64String.startsWith('/9j/') || 
         base64String.startsWith('/9j/4AAQSkZJRgABAQAAAQABAAD');
};

/**
 * Processes an image string and returns a proper data URL
 * @param imageString - The image string (base64, data URL, or URL)
 * @returns Properly formatted data URL or original URL
 */
export const processImageString = (imageString: string | null | undefined): string | null => {
  if (!imageString || typeof imageString !== 'string') {
    return null;
  }

  // Handle data URLs (already properly formatted)
  if (imageString.startsWith('data:')) {
    return imageString;
  }

  // Handle URLs
  if (imageString.startsWith('http')) {
    return imageString;
  }

  // Handle base64 strings without data URL prefix
  if (imageString.length > 100 && !imageString.includes('http')) {
    const format = isPngBase64(imageString) ? 'image/png' : 'image/jpeg';
    return `data:${format};base64,${imageString}`;
  }

  return null;
};

/**
 * Gets CSS styles to ensure image transparency is preserved
 * @returns CSS style object for transparent images
 */
export const getTransparentImageStyles = () => ({
  backgroundColor: 'transparent',
  background: 'none',
  backgroundImage: 'none'
});

/**
 * Checks if an image format supports transparency
 * @param imageString - The image string to check
 * @returns boolean indicating if the format supports transparency
 */
export const supportsTransparency = (imageString: string): boolean => {
  if (imageString.startsWith('data:image/png')) {
    return true;
  }
  
  if (imageString.startsWith('data:image/gif')) {
    return true;
  }
  
  if (imageString.startsWith('data:image/webp')) {
    return true;
  }
  
  // Check base64 content for PNG
  if (imageString.length > 100 && !imageString.includes('http') && !imageString.startsWith('data:')) {
    return isPngBase64(imageString);
  }
  
  return false;
};
